// OffersCompare — dedicated route for cross-offer analysis.
//
// Phase 14h split: the dashboard /offers stays focused on browsing each
// offer's details (cards + per-offer MarketBar + Negotiate). All
// CROSS-offer artifacts (6-dim grid, LLM-driven compare_offers advisor)
// moved here so the dashboard reads clean.
//
// Source-of-truth for offers is still localStorage `nice2job.offers`. The
// `offers-compare` useJobRunner key is unchanged so any in-flight or
// completed analysis from before the split is still attached.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, ArrowLeft } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import Progress from '../components/ui/Progress';
import { apiGet } from '../lib/api';
import { adaptDataResponse } from '../lib/adapt';
import { useJobRunner } from '../lib/useJobRunner';
import { cleanLLMOutput } from '../lib/clean-llm-output';
import type { Application, Offer, OfferScores } from '../lib/types';

const STORAGE_KEY = 'nice2job.offers';
const DIMS: (keyof OfferScores)[] = ['mission', 'growth', 'culture', 'comp', 'pace', 'remote'];

function loadOffers(): Offer[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Offer[];
    // Phase 14k: drop rejected offers from this view entirely. Comparing
    // a rejected offer against active ones doesn't help the decision —
    // the candidate has explicitly taken it off the table. The LLM
    // advisor also wastes tokens ranking what's already dead.
    return parsed.filter((o) => o.status !== 'rejected');
  } catch { return []; }
}

function companyKey(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

interface PendingOfferCard { kind: 'pending'; app: Application }
interface FullOfferCard { kind: 'full'; offer: Offer }
type OfferCard = PendingOfferCard | FullOfferCard;

export default function OffersCompare() {
  const nav = useNavigate();
  const [offers, setOffers] = useState<Offer[]>([]);
  useEffect(() => setOffers(loadOffers()), []);

  const dataQ = useQuery({
    queryKey: ['data'],
    queryFn: async () => adaptDataResponse(await apiGet('/api/data')),
  });
  const offerStageApps = (dataQ.data?.applications ?? []).filter((a) => a.stage === 'Offer');

  const cards: OfferCard[] = useMemo(() => {
    const manualByCompany = new Map<string, Offer>();
    for (const o of offers) manualByCompany.set(companyKey(o.company), o);
    const fullCards: OfferCard[] = offers.map((o) => ({ kind: 'full' as const, offer: o }));
    const pendingCards: OfferCard[] = offerStageApps
      .filter((a) => !manualByCompany.has(companyKey(a.company)))
      .map((app) => ({ kind: 'pending' as const, app }));
    return [...fullCards, ...pendingCards];
  }, [offers, offerStageApps]);

  // LLM offer comparison — same useJobRunner key as before so any prior
  // analysis (kicked off from the OLD merged page) is still here when the
  // user navigates to /offers/compare for the first time post-split.
  // Reattach: compare_offers is a per-user singleton, so adopt whatever
  // is running on the server for this user — no payload filter.
  const compareRunner = useJobRunner('offers-compare', { reattach: { cmd: 'compare_offers' } });
  const runCompare = () => {
    if (offers.length < 2) return;
    const payload = offers.map((o) => {
      // Pull cached benchmark from localStorage if any (same key
      // useSalaryBenchmark uses on the dashboard) so the prompt sees
      // market-gap signals without re-running salary_benchmark here.
      let benchmark: { p25: number; p50: number; p75: number; confidence: string; reasoning?: string } | undefined;
      try {
        const raw = localStorage.getItem(`nice2job.benchmark.${o.id}`);
        if (raw) {
          const c = JSON.parse(raw);
          if (typeof c.p25 === 'number' && typeof c.p50 === 'number' && typeof c.p75 === 'number') {
            benchmark = { p25: c.p25, p50: c.p50, p75: c.p75, confidence: c.confidence || 'medium', reasoning: c.reasoning };
          }
        }
      } catch { /* noop */ }
      return {
        company: o.company,
        role: o.role,
        location: o.location,
        base: o.base,
        equity: o.equity,
        bonus: o.bonus,
        total: o.total,
        scores: o.scores,
        deadline: o.deadline,
        notes: o.notes,
        ...(benchmark ? { benchmark } : {}),
      };
    });
    compareRunner.startJob('compare_offers', payload);
  };
  const compareMarkdown = useMemo(() => cleanLLMOutput(compareRunner.log || ''), [compareRunner.log]);

  // ── Empty state: <2 offers can't be compared ──────────────────────────
  if (offers.length < 2) {
    return (
      <div className="page-enter">
        <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav('/offers')}>
          <ArrowLeft size={12} style={{ marginRight: 4, display: 'inline' }} /> Offers
        </button>
        <PageHeader
          eyebrow="Compare offers"
          title="Need at least two."
          sub={`You currently have ${offers.length} offer${offers.length === 1 ? '' : 's'} on file. Add another to run a side-by-side comparison and AI advisor.`}
        />
        <div className="card empty" style={{ padding: 28 }}>
          <span className="serif">Comparison needs ≥2 offers.</span>
          Go back to Offers and add a second offer to unlock the 6-dimension grid + LLM advisor.
          <div style={{ marginTop: 18 }}>
            <button className="btn accent" onClick={() => nav('/offers')}>← Back to Offers</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav('/offers')}>
        <ArrowLeft size={12} style={{ marginRight: 4, display: 'inline' }} /> Offers
      </button>

      <PageHeader
        eyebrow="Compare & analyze"
        title={
          <>
            Comparing {offers.length} offers.{' '}
            <em style={{ fontStyle: 'italic', color: 'var(--pri)' }}>One decision.</em>
          </>
        }
        sub="Self-scored 0–5 across mission / growth / culture / comp / pace / remote. The AI advisor weighs these against market gaps + your CV to recommend a final path."
      />

      {/* ── 6-dimension cross-offer grid ──────────────────────────────────── */}
      <div className="card" style={{ padding: 22, marginBottom: 16 }}>
        <div className="serif" style={{ fontSize: 22, marginBottom: 4 }}>Fit across six dimensions</div>
        <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 22 }}>
          Your self-scored 0–5 against each.
          {cards.some((c) => c.kind === 'pending') && (
            <span style={{ marginLeft: 8 }}>
              Pipeline offers without comp details show <code>—</code>; fill them in on the Offers page.
            </span>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(${cards.length},1fr)`, gap: 10, alignItems: 'center' }}>
          <div />
          {cards.map((c) => {
            const company = c.kind === 'full' ? c.offer.company : c.app.company;
            const pending = c.kind === 'pending';
            const keyId = c.kind === 'full' ? c.offer.id : `pending-${c.app.id || c.app.num}`;
            return (
              <div key={keyId} style={{ fontSize: 12, fontWeight: 500, color: pending ? 'var(--ink3)' : 'var(--ink)' }}>
                {company}
                {pending && <span style={{ fontSize: 10, marginLeft: 6, color: 'var(--ink3)' }}>· pending</span>}
              </div>
            );
          })}
          {DIMS.map((k) => (
            // Use React.Fragment with key to satisfy lint/runtime — `<>` can't take a key.
            <DimRow
              key={k}
              dim={k}
              cards={cards}
              onPendingClick={() => nav('/offers')}
            />
          ))}
        </div>
      </div>

      {/* ── LLM advisor ─────────────────────────────────────────────────── */}
      <div className="card" style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="serif" style={{ fontSize: 22 }}>AI advisor</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 2 }}>
              Reads your offers (with cached market benchmarks) + your CV, produces a market-gap table, weighted scoring, ranking, and a final recommendation including BATNA framing.
            </div>
          </div>
          <button
            className="btn accent"
            onClick={runCompare}
            disabled={compareRunner.isRunning}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Sparkles size={14} />
            {compareRunner.isRunning ? 'Analyzing…' : compareMarkdown ? 'Re-analyze' : 'Analyze with AI'}
          </button>
        </div>

        {compareRunner.isActive && (
          <>
            <Progress
              value={compareRunner.status === 'done' ? 100 : compareRunner.status === 'failed' ? 100 : Math.min(95, compareRunner.log.length / 40)}
            />
            <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6, marginBottom: 14 }}>
              Job {compareRunner.jobId?.slice(0, 8)} · {compareRunner.status} · {compareRunner.log.length.toLocaleString()} chars
            </div>
          </>
        )}

        {compareRunner.errorLine && (
          <div style={{ padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)', marginBottom: 12 }}>
            {compareRunner.errorLine}
          </div>
        )}

        {compareMarkdown && (
          <div className="report-md" style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--ink)' }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{compareMarkdown}</ReactMarkdown>
          </div>
        )}

        {compareRunner.status === 'done' && (
          <div style={{ marginTop: 12 }}>
            <button className="btn ghost" onClick={compareRunner.dismiss}>Clear</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper: one row of the 6-dim grid (label + per-offer cells). Extracted so
// the parent map's React.Fragment with a key issue (which `<>` can't accept)
// becomes irrelevant — each row is just a regular component instance.
function DimRow({
  dim, cards, onPendingClick,
}: {
  dim: keyof OfferScores;
  cards: OfferCard[];
  onPendingClick: () => void;
}) {
  return (
    <>
      <div className="tab">{dim}</div>
      {cards.map((c) => {
        if (c.kind === 'full') {
          const o = c.offer;
          return (
            <div key={o.id + dim} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Progress value={o.scores[dim] * 20} color={o.scores[dim] > 4.5 ? 'var(--pri)' : 'var(--accent)'} style={{ flex: 1 }} />
              <div style={{ fontSize: 11, color: 'var(--ink2)', width: 26 }}>{o.scores[dim].toFixed(1)}</div>
            </div>
          );
        }
        const a = c.app;
        return (
          <div
            key={`pending-${a.id || a.num}-${dim}`}
            onClick={onPendingClick}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              color: 'var(--ink3)', fontSize: 12, cursor: 'pointer', opacity: 0.7,
            }}
            title="Click to add comp details on the Offers page"
          >
            <Progress value={0} color="var(--line)" style={{ flex: 1 }} />
            <div style={{ width: 26, textAlign: 'right' }}>—</div>
          </div>
        );
      })}
    </>
  );
}
